import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, Truck, Sparkles } from 'lucide-react';

import AllergenNotice from '../../components/public/AllergenNotice';
import InquiryForm from '../../components/public/InquiryForm';
import Faq from '../../components/public/Faq';
import PublicLayout from '../../components/public/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { business, cateringFaq, cateringPackages } from '../../content/siteContent.mjs';

const HOW_IT_WORKS = [
  {
    icon: CalendarClock,
    title: 'Pick a date and a size',
    body: `Give us at least ${business.leadTime}. Donut walls and 100+ runs are best booked a week ahead.`,
  },
  {
    icon: Sparkles,
    title: 'We build the box',
    body: 'A baker’s assortment by default, or tell us the flavours your guests will actually fight over.',
  },
  {
    icon: Truck,
    title: 'Delivered in Westchester',
    body: 'Delivery is included in the package price — tell us the town and the time you need them on the table.',
  },
];

const Catering = () => {
  const [selectedPackage, setSelectedPackage] = useState('');

  const chooseAndScroll = (name) => {
    setSelectedPackage(name);
    const el = document.getElementById('quote');
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96, behavior: 'smooth' });
  };

  return (
    <PublicLayout testId="public-catering">
      <section className="pt-16 pb-14 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-amber-600">
            Offices &amp; events
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Donut catering for offices and events
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            Breakfast meetings, all-hands, launches, weddings and donut walls — three packages from 24 to 100+
            donuts, baked to order and delivered across Westchester.
          </p>
          <Button
            size="lg"
            className="bg-gray-900 text-white hover:bg-gray-800 text-lg px-8 py-6"
            onClick={() => chooseAndScroll('')}
            data-testid="catering-hero-cta"
          >
            Request a quote
          </Button>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
            Catering packages
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cateringPackages.map((pkg) => (
              <Card
                key={pkg.id}
                className="hover:shadow-xl transition-shadow duration-300"
                data-testid={`catering-package-${pkg.id}`}
              >
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{pkg.description}</p>
                  <div className="space-y-2 mb-6">
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold">Serves:</span> {pkg.servings}
                    </p>
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold">Includes:</span> {pkg.includes}
                    </p>
                  </div>
                  <p className="text-3xl font-bold text-amber-600 mb-6">{pkg.price}</p>
                  <Button
                    className="w-full bg-gray-900 text-white hover:bg-gray-800"
                    onClick={() => chooseAndScroll(pkg.name)}
                    data-testid={`catering-quote-${pkg.id}`}
                  >
                    Request quote
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-gray-500">
            Prices are a starting point — the final quote depends on flavours, quantity and delivery town.
          </p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-amber-50/60">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="p-8 bg-white text-center hover:shadow-xl transition-shadow duration-300">
                <div className="flex justify-center mb-6">
                  <div className="bg-amber-100 p-4 rounded-full">
                    <Icon size={28} className="text-amber-600" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="quote" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 scroll-mt-24">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Request a catering quote</h2>
            <p className="text-lg text-gray-600">
              One minute to fill in, one business day to hear back with a price and available dates.
            </p>
          </div>
          <InquiryForm
            key={selectedPackage || 'blank'}
            variant="catering"
            initialValues={{ package: selectedPackage }}
            testId="catering-quote-form"
          />
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">
            Catering questions
          </h2>
          <Faq items={cateringFaq} testIdPrefix="catering-faq" />
        </div>
      </section>

      <AllergenNotice />

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5">Ordering every week instead?</h2>
          <p className="text-lg text-gray-300 mb-9">
            Cafés, restaurants and offices with a standing order get wholesale pricing and their own ordering
            portal.
          </p>
          <Button asChild size="lg" className="bg-amber-500 text-gray-900 hover:bg-amber-400 text-lg px-8 py-6">
            <Link to="/wholesale#inquiry">Look at wholesale supply</Link>
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Catering;
